import { useEffect, useState } from "react";
import { DatePicker, Select, Space, Table } from "antd";
import dayjs from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { TableDownload } from "../../components/TableDownload";
import { ColumnPicker, useColumnPicker } from "../../components/ColumnPicker";
import { PrintFilters } from "../../components/PrintFilters";
import { api } from "../../helpers/api";
import { useStockLocationsQuery } from "../../helpers/queries";
import { DT_FORMATS } from "../../helpers/constants";
import type { ExportColumns } from "../../helpers/csv";
import type { StockLedgerEntry } from "../../api-models";

const { RangePicker } = DatePicker;

// One definition behind both the table and its export.
const COLUMNS: ExportColumns<StockLedgerEntry> = [
  { title: "Date", dataIndex: "createdAt", render: (v) => dayjs(v).format(DT_FORMATS.dateTime) },
  { title: "Location", render: (_, r) => r.location?.name },
  { title: "Code", render: (_, r) => r.item?.code },
  { title: "Item", render: (_, r) => r.item?.name },
  { title: "Type", dataIndex: "movementType" },
  { title: "Qty", dataIndex: "qtyDelta" },
  { title: "Balance after", dataIndex: "balanceAfter" },
];

export default function StockMovementReportPage() {
  const { data: locations = [] } = useStockLocationsQuery();
  const [locationId, setLocationId] = useState<number>();
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [rows, setRows] = useState<StockLedgerEntry[]>([]);
  const cols = useColumnPicker("report:stock-movement", COLUMNS);

  useEffect(() => {
    api
      .get<StockLedgerEntry[]>("/reports/stock-movement", {
        params: { locationId, from: range?.[0]?.toISOString(), to: range?.[1]?.toISOString() },
      })
      .then((res) => setRows(res.data));
  }, [locationId, range]);

  // One description of the view, read by both the export header and the printed page.
  const filters = {
    location: locations.find((w) => w.id === locationId)?.name ?? "All locations",
    period: range ? `${range[0].format(DT_FORMATS.date)} — ${range[1].format(DT_FORMATS.date)}` : "All dates",
  };

  return (
    <div>
      <PageHeader
        title="Stock Movement"
        extra={
          <Space>
            <ColumnPicker state={cols} />
            <TableDownload
              name="Stock Movement"
              columns={cols.visible}
              dataSource={rows}
              disabled={!rows.length}
              filters={filters}
            />
          </Space>
        }
      />
      <div className="glut-filter-bar">
        <Select
          allowClear
          placeholder="Location"
          style={{ width: 220 }}
          options={locations.map((w) => ({ value: w.id, label: w.name }))}
          value={locationId}
          onChange={setLocationId}
        />
        <RangePicker onChange={(v) => setRange(v as [dayjs.Dayjs, dayjs.Dayjs] | null)} />
      </div>
      <PrintFilters filters={filters} />
      <Table rowKey="id" dataSource={rows} columns={cols.visible} />
    </div>
  );
}
